import type { ComponentProps, ReactNode } from 'react';
import { useId } from 'react';
import { cn } from '@/lib/utils';

/**
 * MarketNest text input: 42px tall, 4px radius, a 1.5px warm-grey border that
 * turns terracotta on focus with a 15% ring. Error swaps both to red.
 */
const controlClasses =
  'h-[42px] w-full rounded-control border-[1.5px] border-border-strong bg-surface px-3.5 ' +
  'text-body font-normal text-ink placeholder:text-ink-faint transition-colors ' +
  'focus-visible:border-brand focus-visible:outline-3 focus-visible:outline-offset-0 ' +
  'focus-visible:outline-brand/15 ' +
  'aria-invalid:border-danger aria-invalid:focus-visible:border-danger ' +
  'aria-invalid:focus-visible:outline-danger/15 ' +
  'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:opacity-50';

export function Input({ className, ...props }: ComponentProps<'input'>) {
  return <input className={cn(controlClasses, className)} {...props} />;
}

export function Select({ className, ...props }: ComponentProps<'select'>) {
  return <select className={cn(controlClasses, className)} {...props} />;
}

/**
 * Label + control + error, wired together by a generated id so the label is
 * always clickable and screen readers announce the error with the field.
 */
export function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string;
  hint?: ReactNode;
  children: (props: {
    id: string;
    'aria-describedby'?: string;
    'aria-invalid'?: boolean;
  }) => ReactNode;
}) {
  const id = useId();
  const messageId = error || hint ? `${id}-message` : undefined;

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-[13px] font-semibold text-ink">
        {label}
      </label>
      {children({ id, 'aria-describedby': messageId, 'aria-invalid': error ? true : undefined })}
      {(error || hint) && (
        <p
          id={messageId}
          className={cn('text-caption font-normal', error ? 'text-danger' : 'text-ink-muted')}
        >
          {error ?? hint}
        </p>
      )}
    </div>
  );
}
